import { Button } from '@gearbelt/ui/components';

import { Checkbox } from '@/components/ui/checkbox';

import CheckIcon from './icons/check';
import WarningTriangleIcon from './icons/warning-triangle';

type ExportProgressViewProps = {
  status: 'generating' | 'ready' | 'error';
  progress: number;
  label: string;
  downloadUrl: string | null;
  autoDownload: boolean;
  onAutoDownloadChange: (value: boolean) => void;
  onDownload: () => void;
  onRetry: () => void;
};

/**
 * Full-screen export progress screen shown in the dedicated export tab (Dashpivot's
 * pattern). Unlike the old blocking dialog it never traps the user — the originating
 * Gearbelt tab stays fully usable while this one renders progress, the ready state, or
 * an error.
 */
export function ExportProgressView({ status, progress, label, downloadUrl, autoDownload, onAutoDownloadChange, onDownload, onRetry }: ExportProgressViewProps) {
  return (
    <div className="bg-grey-1 flex min-h-[100dvh] items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-[10px] rounded-lg bg-white p-8 text-center shadow-sm">
        {status === 'generating' && (
          <>
            <h1 className="text-default-text text-lg font-medium">Downloading Files</h1>
            <p className="text-default-text text-sm">Rendering...</p>
            <div
              className="bg-grey-2 relative h-6 w-full overflow-hidden rounded"
              role="status"
              aria-live="polite"
              aria-label={`Generating ${label}, ${progress}%`}>
              {/* Inline width + clip-path are the documented exception for dynamic percentages. */}
              <div
                className="bg-default-blue absolute inset-y-0 left-0 transition-[width] duration-300 ease-out"
                style={{ width: `${progress}%` }}
                aria-hidden
              />
              {/* The percentage is drawn twice so it stays legible on both the track and the fill:
                  a dark copy over the grey track, and a white copy clipped to the filled width. */}
              <span aria-hidden className="text-default-text absolute inset-0 flex items-center justify-center text-xs font-medium tabular-nums">
                {progress}%
              </span>
              <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white tabular-nums"
                style={{ clipPath: `inset(0 ${100 - progress}% 0 0)` }}>
                {progress}%
              </span>
            </div>
            <p className="text-default-text text-sm">This may take some time. You can keep working in Gearbelt — this tab will update on its own.</p>
            <div className="flex items-center gap-2">
              <Checkbox id="export-auto-download" checked={autoDownload} onCheckedChange={(value) => onAutoDownloadChange(value === true)} />
              <label htmlFor="export-auto-download" className="text-default-text cursor-pointer text-sm">
                Automatically download when ready
              </label>
            </div>
          </>
        )}

        {status === 'ready' && (
          <>
            <h1 className="text-default-text text-lg font-medium">Download Ready</h1>
            <CheckIcon size={40} className="text-default-green" />
            <p className="text-default-text text-sm">
              Thanks for using Gearbelt. Your download should have started automatically. If not, click below to download.
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <Button variant="default" disabled={!downloadUrl} onClick={onDownload}>
                Download
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-default-text text-lg font-medium">Something went wrong</h1>
            <WarningTriangleIcon size={40} className="text-default-red" />
            <p className="text-default-text text-sm">{`We couldn't prepare your ${label} download. Please try again — you can safely close this tab if you'd rather retry from Gearbelt.`}</p>
            <div className="mt-4 flex justify-center gap-2">
              <Button variant="default" onClick={onRetry}>
                Try again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
